import React, { useState, useEffect, useCallback } from 'react';
import { Tag, Plus, Trash2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { useAdminSettings } from '../../../hooks/useAdminSettings';

const CategoriesTab = () => {
  const { apiCall, loading, setLoading, showSuccess, showError } = useAdminSettings();
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: '', icon: '' });

  const fetchCategories = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/admin/settings/categories');
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleAddCategory = async () => {
    if (!newCategory.name) return;
    
    setLoading(true);
    try {
      await apiCall('post', '/api/admin/settings/categories', {
        name: newCategory.name,
        icon: newCategory.icon || '📁'
      });
      showSuccess('Category added');
      setNewCategory({ name: '', icon: '' });
      fetchCategories();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to add category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category?')) return;
    
    try {
      await apiCall('delete', `/api/admin/settings/categories/${categoryId}`);
      showSuccess('Category deleted');
      fetchCategories();
    } catch (error) {
      showError('Failed to delete category');
    }
  };

  return (
    <div className="space-y-6">
      {/* Add Category */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Add Category</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Input
              value={newCategory.name}
              onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
              placeholder="Category name"
              className="bg-gray-800 border-gray-700 text-white flex-1"
            />
            <Input
              value={newCategory.icon}
              onChange={(e) => setNewCategory({...newCategory, icon: e.target.value})}
              placeholder="Icon (emoji)"
              className="bg-gray-800 border-gray-700 text-white w-24"
            />
            <Button 
              onClick={handleAddCategory} 
              disabled={loading || !newCategory.name}
              className="bg-yellow-500 hover:bg-yellow-600 text-black disabled:opacity-50"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Categories List */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Content Categories ({categories.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category) => (
              <div key={category.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{category.icon}</span>
                  <span className="text-white">{category.name}</span>
                </div>
                <Button
                  onClick={() => handleDeleteCategory(category.id)}
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CategoriesTab;
